import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDate } from "@/lib/api";

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const seen = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!alive) return;
        if (seen.current) {
          data.filter((n) => !seen.current.has(n.id)).forEach((n) =>
            toast(n.title, { description: n.message, duration: 9000 })
          );
        }
        seen.current = new Set(data.map((n) => n.id));
        setItems(data);
      } catch { /* ignore polling errors */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const unread = items.filter((n) => !n.read).length;
  const onOpen = async (open) => {
    if (open && unread) {
      await api.post("/notifications/read");
      setItems((xs) => xs.map((n) => ({ ...n, read: true })));
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-full p-2.5 text-amber-100/80 transition-colors hover:bg-white/5 hover:text-amber-200" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-black">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,360px)] border-amber-500/20 bg-[#121216]/95 p-0 backdrop-blur-xl">
        <div className="border-b border-white/5 px-4 py-3 lux-eyebrow">Messages</div>
        <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 && <p className="px-4 py-6 text-sm text-zinc-500">No messages yet.</p>}
          {items.map((n) => (
            <div key={n.id} data-testid="notification-item" className="border-b border-white/5 px-4 py-3 last:border-0">
              <p className="text-sm font-semibold text-amber-100">{n.title}</p>
              <p className="mt-1 text-sm text-zinc-300">{n.message}</p>
              <p className="mt-1 text-xs text-zinc-500">{fmtDate(n.created_at)}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
